import React from "react";
import { useLang } from "@/contexts/LangContext";
import { useNavigate, useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import BackToTopButton from "@/components/BackToTopButton";
import VerseRow from "@/components/bible/VerseRow";
import BibleAccordionIndex from "@/components/bible/BibleAccordionIndex";
import SelectionActionBar from "@/components/SelectionActionBar";
import { useSelection } from "@/lib/useSelection";
import { useHighlightedFavourites } from "@/hooks/useHighlightedFavourites";
import {
    loadBible, getCachedBible, getFlatIndex,
    displayName, parseReference,
} from "@/lib/bibleData";
import { highlightText } from "@/lib/textSearch";
import {
    MagnifyingGlass, X, CaretLeft, CaretRight, ListBullets,
} from "@phosphor-icons/react";

const PAGE_SIZE = 20;

export default function Bible() {
    const { t, lang } = useLang();
    const navigate = useNavigate();
    const location = useLocation();

    const [data, setData] = React.useState(() => getCachedBible(lang));
    const [loading, setLoading] = React.useState(() => !getCachedBible(lang));
    const [error, setError] = React.useState("");
    const [bookIdx, setBookIdx] = React.useState(0);
    const [chapter, setChapter] = React.useState(1);
    const [query, setQuery] = React.useState("");
    const [visible, setVisible] = React.useState(PAGE_SIZE);
    // View mode: "index" shows the BibleAccordionIndex of all 73 books;
    // "reading" shows the chapter pane. Search is a separate render
    // branch driven by `query`, independent of this state.
    const [view, setView] = React.useState("index");
    // Multi-row selection: long-press starts/re-anchors; short-click
    // toggles (only when in selection mode); double-click clears.
    // Keys are formed as `${book}|${chapter}|${verse}`.
    const { selectedKeys, inSelectionMode, startOrReset, toggle, clear } = useSelection();
    const sentinelRef = React.useRef(null);

    // Load (or swap) the JSON whenever the language changes.
    React.useEffect(() => {
        let cancelled = false;
        setQuery("");
        setBookIdx(0);
        setChapter(1);
        if (getCachedBible(lang)) {
            setData(getCachedBible(lang));
            setLoading(false);
            setError("");
            return undefined;
        }
        setLoading(true);
        setError("");
        loadBible(lang)
            .then((d) => { if (!cancelled) setData(d); })
            .catch((e) => { if (!cancelled) setError(e.message || "Failed to load"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [lang]);

    // One-shot fetch of the user's existing bible-verse favorites so each
    // row can light up its "saved" indicator right after data is loaded.
    const { saved: savedVerses, applyHighlight, clearHighlight, getRowColor } =
        useHighlightedFavourites({
            section: "bible",
            filterFavorite: (f) => {
                const meta = f.metadata || {};
                if (meta.kind !== "verse") return false;
                if (meta.lang && meta.lang !== lang) return false;
                return true;
            },
            keyFromFavorite: (f) => {
                const meta = f.metadata || {};
                return `${meta.book}|${meta.chapter}|${meta.verse}`;
            },
            buildPayload: async (key) => {
                const [bookName, chStr, vStr] = key.split("|");
                if (!data) return null;
                const book = data.books.find((b) => b.name === bookName);
                if (!book) return null;
                const ch = book.chapters.find((c) => String(c.chapter) === chStr);
                if (!ch) return null;
                const verse = ch.verses.find((v) => String(v.verse) === vStr);
                if (!verse) return null;
                return {
                    title: `${displayName(book.name, lang)} ${ch.chapter}:${verse.verse}`,
                    content: verse.text,
                    metadata: {
                        kind: "verse",
                        book: book.name,
                        chapter: ch.chapter,
                        verse: verse.verse,
                        lang,
                    },
                };
            },
            extraDeps: [data],
        });

    // Resolve "book|chapter|verse" keys → ordered list of verse records
    // for the *current chapter*. Selection is always confined to the
    // chapter the user is reading; navigating away clears it.
    const resolveSelectedVerses = React.useCallback((keys) => {
        if (!data) return [];
        const out = [];
        for (const k of keys) {
            const [bookName, chStr, vStr] = k.split("|");
            const book = data.books.find((b) => b.name === bookName);
            if (!book) continue;
            const ch = book.chapters.find((c) => String(c.chapter) === chStr);
            if (!ch) continue;
            const verse = ch.verses.find((v) => String(v.verse) === vStr);
            if (!verse) continue;
            out.push({ book: book.name, chapter: ch.chapter, verse: verse.verse, text: verse.text });
        }
        // Stable order: by chapter then verse number.
        out.sort((a, b) => (a.chapter - b.chapter) || (a.verse - b.verse));
        return out;
    }, [data]);

    const formatSelection = React.useCallback((keys) => {
        const verses = resolveSelectedVerses(keys);
        if (verses.length === 0) return "";
        const lines = verses.map((v) => `${v.verse}. ${v.text}`).join("\n");
        const first = verses[0];
        const last = verses[verses.length - 1];
        const ref = first.verse === last.verse
            ? `${first.book} ${first.chapter}:${first.verse}`
            : `${first.book} ${first.chapter}:${first.verse}-${last.verse}`;
        return `"${lines}"\n— ${ref}`;
    }, [resolveSelectedVerses]);

    const books = React.useMemo(() => data?.books || [], [data]);
    const currentBook = books[bookIdx] || null;
    // Actual chapter numbers as stored in the data — usually 1..N but
    // some translations (e.g. the ES Eclesiástico in the Jerusalén JSON)
    // include a chapter "0" for the foreword. Driving navigation off
    // this list avoids generating buttons for chapters that don't exist.
    const chapterNumbers = React.useMemo(
        () => (currentBook?.chapters || []).map((c) => c.chapter),
        [currentBook],
    );
    const totalChapters = chapterNumbers.length;
    const currentChapterPos = chapterNumbers.indexOf(chapter);
    const canGoPrev = currentChapterPos > 0;
    const canGoNext = currentChapterPos >= 0 && currentChapterPos < chapterNumbers.length - 1;
    const goPrevChapter = React.useCallback(() => {
        if (canGoPrev) setChapter(chapterNumbers[currentChapterPos - 1]);
    }, [canGoPrev, chapterNumbers, currentChapterPos]);
    const goNextChapter = React.useCallback(() => {
        if (canGoNext) setChapter(chapterNumbers[currentChapterPos + 1]);
    }, [canGoNext, chapterNumbers, currentChapterPos]);

    // Navigating to a different book or chapter abandons whatever
    // selection the user had on the previous view — stale keys would
    // never resolve and the floating bar would feel ghost-like.
    React.useEffect(() => {
        clear();
    }, [bookIdx, chapter, clear]);

    // Continuous text search.
    const results = React.useMemo(() => {
        const q = query.trim();
        if (!q || !data) return [];
        const idx = getFlatIndex(lang, data);
        const needle = q.toLowerCase();
        return idx.filter((v) => v.text.toLowerCase().includes(needle));
    }, [query, data, lang]);

    const refSuggestion = React.useMemo(
        () => parseReference(query, books),
        [query, books],
    );

    const jumpToReference = React.useCallback((book, chap, verse) => {
        const idx = books.findIndex((b) => b.name === book.name);
        if (idx < 0) return;
        setBookIdx(idx);
        setChapter(Math.min(book.chapters.length, chap));
        setQuery("");
        setView("reading");
        requestAnimationFrame(() => {
            if (verse) {
                const el = document.querySelector(`[data-bible-verse="${verse}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                else window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        });
    }, [books]);

    // Accordion picker: jump to chapter and switch to reading view.
    const onPickFromIndex = React.useCallback((idx, chap) => {
        setBookIdx(idx);
        setChapter(chap);
        setView("reading");
        const book = data?.books?.[idx];
        trackEvent("bible_book_select", {
            book: book?.name,
            book_id: book?.id ?? idx,
            chapter: chap,
            lang,
        });
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }, [data, lang]);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        if (refSuggestion) {
            jumpToReference(refSuggestion.book, refSuggestion.chapter, refSuggestion.verse);
        }
    };

    // Deep-link handler — ?ref=Book|Chapter|Verse (from Verse of the Day
    // card on the Dashboard). Runs once the Bible data is ready, then
    // strips the param from the URL so a user reload doesn't re-scroll.
    React.useEffect(() => {
        if (!data) return;
        const params = new URLSearchParams(location.search);
        const ref = params.get("ref");
        if (!ref) return;
        const parts = ref.split("|");
        if (parts.length < 2) return;
        const [bookName, chapStr, verseStr] = parts;
        const book = books.find((b) => b.name === bookName);
        if (!book) return;
        const chap = parseInt(chapStr, 10);
        const verse = verseStr ? parseInt(verseStr, 10) : null;
        if (!Number.isFinite(chap)) return;
        jumpToReference(book, chap, verse);
        // Strip the query so a refresh lands on the current chapter, not
        // an endless re-scroll loop.
        navigate(location.pathname, { replace: true });
    }, [data, books, location.search, location.pathname, jumpToReference, navigate]);

    React.useEffect(() => { setVisible(PAGE_SIZE); }, [query]);

    React.useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return undefined;
        const observer = new IntersectionObserver((obs) => {
            if (obs.some((e) => e.isIntersecting)) {
                setVisible((v) => Math.min(results.length, v + PAGE_SIZE));
            }
        }, { rootMargin: "200px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [results.length]);

    const onResetTop = () => setQuery("");

    const currentChapterVerses = React.useMemo(() => {
        if (!currentBook) return [];
        const ch = currentBook.chapters.find((c) => c.chapter === chapter);
        return ch?.verses || [];
    }, [currentBook, chapter]);

    const shown = results.slice(0, visible);
    const hasMore = visible < results.length;
    const searching = query.trim().length > 0;
    const currentBookDisplay = currentBook ? displayName(currentBook.name, lang) : "";

    return (
        <div data-testid="bible-page">
            <p className="label-eyebrow mb-3">{t("nav.bible")}</p>
            <h1 className="heading-serif text-4xl sm:text-5xl tracking-tight leading-none mb-2">
                {t("nav.bible")}
            </h1>
            <p className="text-sm text-stoneMuted italic mb-6" data-testid="bible-translation-label">
                {data?.translation || ""}
            </p>

            {/* Sticky search bar */}
            <div
                className="sticky top-[56px] md:top-[72px] z-20 -mx-4 sm:-mx-6 lg:-mx-12 px-4 sm:px-6 lg:px-12 py-3 bg-sand-50/95 backdrop-blur-md border-b border-sand-300 mb-8"
                data-testid="bible-search-wrap"
            >
                <form onSubmit={onSearchSubmit} className="relative max-w-[720px] mx-auto" data-testid="bible-search-form">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneFaint" />
                    <input
                        type="search"
                        inputMode="search"
                        placeholder={t("bible.search_placeholder")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="bible-search-input"
                        className="w-full pl-10 pr-10 py-3 bg-white border border-sand-300 rounded-md focus:outline-none focus:border-sangre transition-colors ui-sans text-sm"
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => setQuery("")}
                            aria-label="Clear"
                            data-testid="bible-search-clear"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-stoneMuted hover:text-sangre"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    )}
                </form>
            </div>

            {loading && <p className="text-stoneMuted" data-testid="bible-loading">{t("common.loading")}</p>}
            {error && <p className="text-sangre" data-testid="bible-error">{error}</p>}

            {!loading && !error && data && (
                <>
                    {!searching ? (
                        <>
                            {view === "index" && (
                                <BibleAccordionIndex
                                    books={books}
                                    onPick={onPickFromIndex}
                                    activeBookIdx={bookIdx}
                                />
                            )}

                            {view === "reading" && (
                                <>
                                    <div
                                        className="flex flex-wrap items-center gap-2 mb-6"
                                        data-testid="bible-reading-toolbar"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setView("index");
                                                requestAnimationFrame(() =>
                                                    window.scrollTo({ top: 0, behavior: "smooth" }),
                                                );
                                            }}
                                            data-testid="bible-back-to-index"
                                            className="inline-flex items-center gap-1.5 px-3 py-2 ui-sans text-xs uppercase tracking-widest font-semibold rounded-md border border-sand-300 text-stoneMuted hover:text-blue-600 hover:border-blue-500 transition-colors"
                                        >
                                            <ListBullets size={14} weight="bold" />
                                            <span>{t("bible.back_to_index")}</span>
                                        </button>
                                        <span className="flex-1" />
                                        <button
                                            onClick={goPrevChapter}
                                            disabled={!canGoPrev}
                                            data-testid="bible-prev-chapter"
                                            className="p-2 border border-sand-300 rounded-md hover:border-sangre disabled:opacity-40"
                                        >
                                            <CaretLeft size={14} weight="bold" />
                                        </button>
                                        <span
                                            className="ui-sans text-xs uppercase tracking-widest text-stoneMuted px-2 tabular-nums"
                                            data-testid="bible-chapter-indicator"
                                        >
                                            {Math.max(1, currentChapterPos + 1)} / {totalChapters}
                                        </span>
                                        <button
                                            onClick={goNextChapter}
                                            disabled={!canGoNext}
                                            data-testid="bible-next-chapter"
                                            className="p-2 border border-sand-300 rounded-md hover:border-sangre disabled:opacity-40"
                                        >
                                            <CaretRight size={14} weight="bold" />
                                        </button>
                                    </div>

                                    <h2
                                        className="heading-serif text-3xl tracking-tight mb-6 border-b border-sand-300 pb-3"
                                        data-testid="bible-chapter-title"
                                    >
                                        {currentBookDisplay} {chapter}
                                    </h2>

                                    <div className="reading-prose" data-testid="bible-verses">
                                        {currentChapterVerses.map((v) => {
                                            const k = currentBook ? `${currentBook.name}|${chapter}|${v.verse}` : "";
                                            return (
                                                <VerseRow
                                                    key={v.verse}
                                                    verse={v}
                                                    isSaved={savedVerses.has(k)}
                                                    savedColor={savedVerses.get(k)?.color || null}
                                                    isSelected={selectedKeys.has(k)}
                                                    inSelectionMode={inSelectionMode}
                                                    onLongPress={() => startOrReset(k)}
                                                    onShortClick={() => toggle(k)}
                                                    onDoubleClick={clear}
                                                />
                                            );
                                        })}
                                        {currentChapterVerses.length === 0 && (
                                            <p className="text-stoneMuted">{t("common.loading")}</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {refSuggestion && (
                                <button
                                    type="button"
                                    onClick={() => jumpToReference(
                                        refSuggestion.book,
                                        refSuggestion.chapter,
                                        refSuggestion.verse,
                                    )}
                                    data-testid="bible-ref-suggestion"
                                    className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-sangre/10 border border-sangre/30 text-sangre rounded-md hover:bg-sangre hover:text-sand-50 transition-colors ui-sans text-sm"
                                >
                                    {t("bible.go_to")}: {displayName(refSuggestion.book.name, lang)} {refSuggestion.chapter}{refSuggestion.verse ? `:${refSuggestion.verse}` : ""}
                                </button>
                            )}

                            <p className="label-eyebrow mb-4" data-testid="bible-results-meta">
                                {t("bible.results_count", { count: results.length })}
                            </p>

                            {results.length === 0 && (
                                <p className="text-stoneMuted italic" data-testid="bible-empty">
                                    {t("bible.no_results")}
                                </p>
                            )}

                            <ul className="reading-prose space-y-6" data-testid="bible-results-list">
                                {shown.map((v) => (
                                    <li
                                        key={`${v.book}-${v.chapter}-${v.verse}`}
                                        data-testid={`bible-result-${v.book}-${v.chapter}-${v.verse}`}
                                        className="group"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const bookObj = books.find((b) => b.name === v.book);
                                                if (bookObj) jumpToReference(bookObj, v.chapter, v.verse);
                                            }}
                                            className="block w-full text-left"
                                        >
                                            <p className="label-eyebrow mb-1 group-hover:text-sangre transition-colors">
                                                {displayName(v.book, lang)} {v.chapter}:{v.verse}
                                            </p>
                                            <p className="m-0">{highlightText(v.text, query)}</p>
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            {hasMore && (
                                <div ref={sentinelRef} className="mt-10 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setVisible((v) => Math.min(results.length, v + PAGE_SIZE))}
                                        data-testid="bible-load-more"
                                        className="btn-ghost"
                                    >
                                        {t("catechism.load_more")} ({visible}/{results.length})
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            <BackToTopButton onClick={onResetTop} testId="bible-back-to-top" />
            <SelectionActionBar
                selectedKeys={selectedKeys}
                format={formatSelection}
                applyHighlight={applyHighlight}
                clearHighlight={clearHighlight}
                getRowColor={getRowColor}
                onClear={clear}
                testIdBase="bible-selection"
            />
        </div>
    );
}
